import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { FormApi } from 'final-form';

import { ChatForm } from './chat-form.component';
import { WelcomeSuggestions } from './welcome-suggestions.component';

import { WELCOME_MESSAGES } from '../../drive-insights.constants';

const gradientShimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const getRandomMessage = () =>
  WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];

const rootSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  overflowY: 'auto',
  zIndex: 1,
};

const contentSx: SxProps<Theme> = {
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 3,
  mx: 'auto',
  px: { xs: 2, md: 3 },
  pt: { xs: '10vh', md: '20vh' },
  pb: 3,
};

const titleContainerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 900,
  mx: 'auto',
};

const titleSx: SxProps<Theme> = {
  fontSize: 44,
  fontWeight: 300,
  textAlign: 'center',
  backgroundImage: 'linear-gradient(to right, #312BCB, #322f83, #403aca, #B800BB)',
  backgroundSize: '200% auto',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  animation: `${gradientShimmer} 12s linear infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  mb: 4,
  py: 0.5,
};

const inputContainerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 700,
  mx: 'auto',
  zIndex: 1,
};

type WelcomeScreenProps = {
  onPromptClick: (prompt: string) => void;
  onSubmit: (values: { question: string }) => void;
  formRef: React.MutableRefObject<FormApi<{ question: string }> | null>;
  isStreaming?: boolean;
};

export const WelcomeScreen = ({
  onPromptClick,
  onSubmit,
  formRef,
  isStreaming,
}: WelcomeScreenProps) => {
  const [welcomeMessage] = useState(getRandomMessage);

  return (
    <Box sx={rootSx}>
      <Box sx={contentSx}>
        <Box sx={titleContainerSx}>
          <Typography variant='h1' component='h1' sx={titleSx}>
            {welcomeMessage}
          </Typography>
        </Box>

        <Box sx={inputContainerSx}>
          <ChatForm
            onSubmit={onSubmit}
            formRef={formRef}
            isStreaming={isStreaming}
            variant='large'
          />
          <Box sx={{ mt: 3 }}>
            <WelcomeSuggestions onPromptClick={onPromptClick} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
